import * as React from 'react';
import {useRef} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS, Icon, SubHeading, Table, Text} from '@forecast-it/design-system';
import {EmptyState} from './EmptyState';
import {ScrollableTable} from './ScrollableTable';
import {formatDateFull} from '../utils/formatDate';
import {ticketingAttachmentDownloadUrl} from '../../../graphql/forecastRelay';

export interface TicketAttachment {
	id: string;
	filename: string;
	contentType: string;
	sizeBytes: number;
	downloadUrl: string;
	createdAt: string;
	uploadedByName?: string | null;
	/** Global id of the Forecast Person who uploaded the file, when there is one. */
	uploadedByPersonId?: string | null;
}

interface Props {
	attachments: readonly TicketAttachment[];
	onUploadFile?: (file: File) => void;
	/** FOR-261: called on confirm-worthy intent — the caller owns the confirmation. */
	onDeleteAttachment?: (id: string, filename: string) => void;
	/**
	 * Whether this viewer may delete this file. The own-vs-others rule lives with the
	 * permission helpers (features/auth/utils/ticketPerms) and is decided by the
	 * caller, which knows the viewer; this component only reads the verdict. Omitted
	 * (or omitted `onDeleteAttachment`) means no delete affordance at all.
	 */
	canDeleteAttachment?: (attachment: TicketAttachment) => boolean;
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Save a file under its own name.
 *
 * The design system's button column gives a plain `onClick`, not an anchor, so the
 * `download` attribute that makes the browser keep the filename has to be applied to
 * a link created for the purpose. Navigating to the URL instead would leave the saved
 * file named after the proxy route.
 */
function saveFile(url: string, filename: string): void {
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.rel = 'noopener';
	document.body.appendChild(link);
	link.click();
	link.remove();
}

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const HiddenInput = styled.input`
	display: none;
`;
const EmptyBody = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;

interface AttachmentRow {
	id: string;
	filename: string;
	size: string;
	uploadedBy: string;
	when: string;
	downloadUrl: string;
	/** Precomputed per row so the column only reads a flag, never the viewer. */
	canDelete: boolean;
}

/**
 * Every file on the ticket, in one table.
 *
 * The design system's `Table` owns the header type, row height, hover fill and
 * alignment; this supplies the column set and the cell content. The paperclip on the
 * name column stands in for a per-type glyph — the set has one paperclip, and
 * inventing a glyph per file extension would mean inventing colours too.
 *
 * Delete (FOR-261) is per-row and permission-scoped: the caller decides which rows
 * the viewer may delete and confirms the intent, so this file holds no auth logic.
 */
export function TicketAttachmentsTab({attachments, onUploadFile, onDeleteAttachment, canDeleteAttachment}: Props) {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleUploadClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file && onUploadFile) {
			onUploadFile(file);
		}
		event.target.value = '';
	};

	const rows: AttachmentRow[] = React.useMemo(
		() =>
			attachments.map(a => ({
				id: a.id,
				filename: a.filename,
				size: formatBytes(a.sizeBytes),
				uploadedBy: a.uploadedByName ?? '—',
				when: formatDateFull(a.createdAt),
				downloadUrl: ticketingAttachmentDownloadUrl(a.downloadUrl),
				canDelete: !!onDeleteAttachment && !!canDeleteAttachment && canDeleteAttachment(a),
			})),
		[attachments, onDeleteAttachment, canDeleteAttachment]
	);

	const isEmpty = rows.length === 0;
	// No point rendering the actions column at all when nothing in the list is
	// deletable — that would be a column of empty overflow menus.
	const showActions = rows.some(row => row.canDelete);

	return (
		<Section data-testid="attachments-tab">
			<HiddenInput ref={fileInputRef} type="file" onChange={handleFileChange} data-testid="attachments-file-input" />

			<Header>
				<SubHeading data-testid="attachments-count">Attachments</SubHeading>
				{!isEmpty && (
					<Button emphasis={EMPHASIS.MONO} icon="upload" onPress={handleUploadClick} data-cy="attachments-upload">
						Upload file
					</Button>
				)}
			</Header>

			{isEmpty ? (
				<EmptyState data-testid="attachments-empty-state">
					<EmptyBody>
						<Text color="auxiliary">No files attached yet</Text>
						<Button
							emphasis={EMPHASIS.MEDIUM}
							icon="upload"
							onPress={handleUploadClick}
							data-cy="attachments-upload-empty"
						>
							Upload file
						</Button>
					</EmptyBody>
				</EmptyState>
			) : (
				<ScrollableTable data-testid="attachments-table-scroll">
					<Table data={rows} getRowId={row => row.id} width="100%" data-cy="attachments-table">
						<Table.TextColumn
							accessorKey="filename"
							header="Name"
							icon={() => <Icon icon="paperclip" size="s" />}
							ellipsis
						/>
						<Table.TextColumn accessorKey="size" header="Size" width="110px" />
						<Table.AvatarColumn accessorKey="uploadedBy" header="Uploaded by" width="200px" />
						<Table.TextColumn accessorKey="when" header="When" width="190px" />
						<Table.ButtonColumn
							header="Download"
							hideHeader
							width="140px"
							button={(row: AttachmentRow) => ({
								label: 'Download',
								icon: 'download',
								onClick: () => saveFile(row.downloadUrl, row.filename),
							})}
						/>
						{showActions && (
							<Table.ActionColumn
								header="Actions"
								hideHeader
								hidden={(row: AttachmentRow) => !row.canDelete}
								align="right"
							>
								<Table.ActionColumn.Option
									hidden={(row: AttachmentRow) => !row.canDelete}
									onClick={(row: AttachmentRow) => onDeleteAttachment?.(row.id, row.filename)}
									data-cy="attachment-delete"
								>
									Delete
								</Table.ActionColumn.Option>
							</Table.ActionColumn>
						)}
					</Table>
				</ScrollableTable>
			)}
		</Section>
	);
}
